// packages/shared/src/seeds/symbol.preset.ts

import {
  AUTO_STATUS,
  AUTO_STATUS_LABELS,
  MANUAL_STATUS,
  MANUAL_STATUS_LABELS,
} from "../constants/symbol.constants";
import {
  COLOR_CELL_DARK_BLUE,
  COLOR_CELL_DARK_RED,
  COLOR_CELL_DARK_VIOLET,
  COLOR_CELL_GRAY,
  COLOR_CELL_GREEN,
  COLOR_CELL_ORANGE,
  COLOR_CELL_RED,
  COLOR_CELL_TURQUOISE,
  COLOR_CELL_VIOLET,
  COLOR_CELL_WHITE,
  COLOR_CELL_YELLOW,
  COLOR_HEADER_DARK_BLUE,
  COLOR_HEADER_DARK_RED,
  COLOR_HEADER_DARK_VIOLET,
  COLOR_HEADER_GRAY,
  COLOR_HEADER_GREEN,
  COLOR_HEADER_ORANGE,
  COLOR_HEADER_RED,
  COLOR_HEADER_TURQUOISE,
  COLOR_HEADER_VIOLET,
  COLOR_HEADER_WHITE,
  COLOR_HEADER_YELLOW,
  COLOR_TEXT_BLACK,
  COLOR_TEXT_DARK_GRAY,
  COLOR_TEXT_WHITE,
} from "../constants/table-colors";
import type { SmartTablePreset } from "../types/smart-table";

export const symbolDefaultPreset: SmartTablePreset = {
  id: "default",
  name: "Default",
  columns: [
    // --- TEMP columns (in TEMP order) ---
    "apiData.fmp.updatedAt",
    "symbol",
    "relations.company.name",
    "relations.tradeCurrency.name",
    "financials.price",
    "relations.exchange.key",
    "financials.years.earliest",
    "financials.years.last",
    "financials.years.sum",
    "financials.incomeStatement.revenue.earliest",
    "financials.incomeStatement.revenue.last",
    "financials.incomeStatement.interestExpense.earliest",
    "financials.incomeStatement.interestExpense.last",
    "financials.incomeStatement.interestExpense.sum",
    "financials.incomeStatement.operatingIncome.earliest",
    "financials.incomeStatement.operatingIncome.last",
    "financials.incomeStatement.netIncome.earliest",
    "financials.incomeStatement.netIncome.last",
    "financials.incomeStatement.weightedAverageShsOutDil.earliest",
    "financials.incomeStatement.weightedAverageShsOutDil.last",
    "financials.incomeStatement.weightedAverageShsOutDil.sum",
    "financials.incomeStatement.eps.earliest",
    "financials.incomeStatement.eps.last",
    "financials.incomeStatement.eps.sum",
    "financials.incomeStatement.operatingIncomePerShare.earliest",
    "financials.incomeStatement.operatingIncomePerShare.last",
    "financials.incomeStatement.operatingIncomePerShare.avg",
    "financials.incomeStatement.operationalMargin.earliest",
    "financials.incomeStatement.operationalMargin.last",
    "financials.incomeStatement.operationalMargin.avg",
    "financials.incomeStatement.netIncomePerShare.earliest",
    "financials.incomeStatement.netIncomePerShare.last",
    "financials.incomeStatement.netIncomePerShare.avg",
    "financials.incomeStatement.netMargin.earliest",
    "financials.incomeStatement.netMargin.last",
    "financials.incomeStatement.netMargin.avg",
    "financials.incomeStatement.interestExpenseToRevenue.earliest",
    "financials.incomeStatement.interestExpenseToRevenue.last",
    "financials.incomeStatement.interestExpenseToRevenue.avg",
    "financials.incomeStatement.interestExpenseToOperatingIncome.earliest",
    "financials.incomeStatement.interestExpenseToOperatingIncome.last",
    "financials.incomeStatement.interestExpenseToOperatingIncome.avg",
    "financials.incomeStatement.interestExpenseToFCF.earliest",
    "financials.incomeStatement.interestExpenseToFCF.last",
    "financials.incomeStatement.interestExpenseToFCF.avg",
    "financials.balance.netDebt.earliest",
    "financials.balance.netDebt.last",
    "financials.balance.totalEquity.earliest",
    "financials.balance.totalEquity.last",
    "financials.balance.debtToEquity.earliest",
    "financials.balance.debtToEquity.last",
    "financials.balance.debtToEquity.avg",
    "financials.balance.debtToShares.earliest",
    "financials.balance.debtToShares.last",
    "financials.balance.debtToShares.avg",
    "financials.balance.equityToSharesRatio.earliest",
    "financials.balance.equityToSharesRatio.last",
    "financials.balance.equityToSharesRatio.avg",
    "financials.cashFlow.freeCashFlow.earliest",
    "financials.cashFlow.freeCashFlow.last",
    "financials.cashFlow.FCFPerShare.earliest",
    "financials.cashFlow.FCFPerShare.last",
    "financials.cashFlow.FCFPerShare.avg",
    "financials.ratios.priceToEarningsRatioTTM",
    "financials.ratios.priceToBookRatioTTM",
    "financials.coefs.ROE.earliest",
    "financials.coefs.ROE.last",
    "financials.coefs.ROE.avg",
    "financials.coefs.netROE.avg",
    "financials.coefs.ROEPriceAdapted.avg",
    "financials.coefs.netROEPriceAdapted.avg",
    "financials.coefs.ROFCFE.earliest",
    "financials.coefs.ROFCFE.last",
    "financials.coefs.ROFCFE.avg",
    "financials.coefs.netROFCFE.avg",
    "financials.coefs.ROFCFEPriceAdapted.avg",
    "financials.coefs.netROFCFEPriceAdapted.avg",
    "financials.coefs.growthRates.netIncomePerShare.avg",
    "financials.coefs.growthRates.netIncome.avg",
    "financials.coefs.growthRates.operatingIncome.avg",
    "financials.coefs.growthRates.FCFPerShare.avg",
    "financials.dealIncome.perNetIncome",
    "financials.dealIncome.perFcf",
    "financials.dealIncome.perFcfAndNetIncome",
    "financials.values.expectedIncomeGrowthRate",
    "financials.values.discountRate",
    "financials.values.marginOfSafety",
    "financials.values.saleCoefficient",
    "financials.incomeType",
    "autoStatus",
    "manualStatus",
    "financials.expectedIncome",
    "financials.expectedIncomeGrowthRate",
    "financials.prices.fair.price1",
    "financials.prices.fair.price2",
    "financials.prices.fair.price3",
    "financials.prices.fair.price4",
    "financials.prices.fair.price5",
    "financials.prices.fair.price6",
    "financials.prices.fair.price7",
    "financials.prices.fair.price8",
    "financials.prices.buy.price1",
    "financials.prices.buy.price2",
    "financials.prices.buy.price3",
    "financials.prices.buy.price4",
    "financials.prices.buy.price5",
    "financials.prices.buy.price6",
    "financials.prices.buy.price7",
    "financials.prices.buy.price8",
    "financials.prices.sell.price1",
    "financials.prices.sell.price2",
    "financials.prices.sell.price3",
    "financials.prices.sell.price4",
    "financials.prices.sell.price5",
    "financials.prices.sell.price6",
    "financials.prices.sell.price7",
    "financials.prices.sell.price8",
    "financials.prices.fair.broad.min",
    "financials.prices.fair.broad.max",
    "financials.prices.buy.broad.min",
    "financials.prices.buy.broad.max",
    "financials.prices.sell.broad.min",
    "financials.prices.sell.broad.max",
    "financials.prices.fair.narrow.min",
    "financials.prices.fair.narrow.max",
    "financials.prices.buy.narrow.min",
    "financials.prices.buy.narrow.max",
    "financials.prices.sell.narrow.min",
    "financials.prices.sell.narrow.max",
    "financials.flags.isInterestingPrice",
    "financials.flags.isNoInterestStatus",
    "financials.flags.isInterestingPriceBroadStatus",
    "financials.flags.isExpensiveStatus",
    "financials.flags.isVeryExpensiveStatus",
    // --- Existing columns not in TEMP (appended at end) ---
    "exchange",
    "price",
    "relations.industry.name",
    "relations.country.name",
  ],
  filters: [],
  sort: [],
  pageSize: 50,
  stickyColumns: ["symbol", "actions"],
  hiddenColumns: [
    "financials.incomeStatement.revenue.earliest",
    "financials.incomeStatement.revenue.last",
    "financials.incomeStatement.interestExpense.earliest",
    "financials.incomeStatement.interestExpense.last",
    "financials.incomeStatement.interestExpense.sum",
    "financials.incomeStatement.operatingIncome.earliest",
    "financials.incomeStatement.operatingIncome.last",
    "financials.incomeStatement.netIncome.earliest",
    "financials.incomeStatement.netIncome.last",
    "financials.incomeStatement.weightedAverageShsOutDil.earliest",
    "financials.incomeStatement.weightedAverageShsOutDil.last",
    "financials.incomeStatement.weightedAverageShsOutDil.sum",
    "financials.incomeStatement.eps.earliest",
    "financials.balance.netDebt.earliest",
    "financials.balance.netDebt.last",
    "financials.balance.totalEquity.earliest",
    "financials.balance.totalEquity.last",
    "financials.cashFlow.freeCashFlow.earliest",
    "financials.cashFlow.freeCashFlow.last",
  ],
  columnMeta: {
    "apiData.fmp.updatedAt": {
      label: {
        "en-US": "API update date (fmp)",
        "ru-RU": "Дата обновления API (fmp)",
      },
      tooltip: {
        "en-US": "API update date (fmp)",
        "ru-RU": "Дата обновления API данных (fmp)",
      },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    symbol: {
      label: { "en-US": "Symbol", "ru-RU": "Тикер" },
      tooltip: { "en-US": "Symbol", "ru-RU": "Тикер" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.company.name": {
      label: { "en-US": "Company name", "ru-RU": "Название компании" },
      tooltip: { "en-US": "Company name", "ru-RU": "Название компании" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.tradeCurrency.name": {
      label: { "en-US": "Currency", "ru-RU": "Валюта" },
      tooltip: { "en-US": "Currency", "ru-RU": "Валюта" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.reportedCurrency.name": {
      label: { "en-US": "Reported Currency", "ru-RU": "Валюта отчетности" },
      tooltip: { "en-US": "Reported Currency", "ru-RU": "Валюта отчетности" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.exchange.key": {
      label: { "en-US": "Exchange", "ru-RU": "Биржа" },
      tooltip: {
        "en-US": "Exchange (Short name) ",
        "ru-RU": "Биржа (короткое название)",
      },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.industry.name": {
      label: { "en-US": "Industry", "ru-RU": "Отрасль" },
      tooltip: { "en-US": "Industry", "ru-RU": "Отрасль" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "relations.country.name": {
      label: { "en-US": "Country", "ru-RU": "Страна" },
      tooltip: { "en-US": "Country", "ru-RU": "Страна" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    exchange: {
      label: { "en-US": "Exchange", "ru-RU": "Биржа" },
      tooltip: { "en-US": "Exchange", "ru-RU": "Биржа" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    autoStatus: {
      label: { "en-US": "Status: System", "ru-RU": "Статус: Система" },
      tooltip: { "en-US": "System status", "ru-RU": "Системный статус" },
      headerColor: COLOR_HEADER_DARK_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    manualStatus: {
      label: { "en-US": "Status: My", "ru-RU": "Статус: Мой" },
      tooltip: { "en-US": "Manual status", "ru-RU": "Ручной статус" },
      headerColor: COLOR_HEADER_DARK_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.price": {
      label: { "en-US": "Price", "ru-RU": "Цена" },
      tooltip: { "en-US": "Price", "ru-RU": "Цена" },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.years.earliest": {
      label: { "en-US": "First year", "ru-RU": "Первый год" },
      tooltip: {
        "en-US": "First calendar year",
        "ru-RU": "Первый календарный год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.years.last": {
      label: { "en-US": "Last year", "ru-RU": "Последний год" },
      tooltip: {
        "en-US": "Last calendar year",
        "ru-RU": "Последний календарный год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.years.sum": {
      label: { "en-US": "Year Numbers", "ru-RU": "Кол-во лет" },
      tooltip: { "en-US": "Year Numbers", "ru-RU": "Кол-во лет" },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.revenue.earliest": {
      label: {
        "en-US": "Revenue (first year)",
        "ru-RU": "Выручка (первый год)",
      },
      tooltip: {
        "en-US": "Revenue for the first year",
        "ru-RU": "Выручка за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.revenue.last": {
      label: {
        "en-US": "Revenue (current year)",
        "ru-RU": "Выручка (текущий год)",
      },
      tooltip: {
        "en-US": "Revenue for the current year",
        "ru-RU": "Выручка за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.revenue.sum": {
      label: { "en-US": "Revenue (sum)", "ru-RU": "Выручка (сумма)" },
      tooltip: {
        "en-US": "Revenue (sum for all years)",
        "ru-RU": "Выручка (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpense.earliest": {
      label: {
        "en-US": "Interest Expense (first year)",
        "ru-RU": "Платежи по долгам (первый год)",
      },
      tooltip: {
        "en-US": "Interest Expense for the first year",
        "ru-RU": "Платежи по долгам за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpense.last": {
      label: {
        "en-US": "Interest Expense (current year)",
        "ru-RU": "Платежи по долгам (текущий год)",
      },
      tooltip: {
        "en-US": "Interest Expense for the current year",
        "ru-RU": "Платежи по долгам за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpense.sum": {
      label: {
        "en-US": "Interest Expense (sum)",
        "ru-RU": "Платежи по долгам (сумма)",
      },
      tooltip: {
        "en-US": "Interest Expense (sum for all years)",
        "ru-RU": "Платежи по долгам (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncome.earliest": {
      label: {
        "en-US": "Operating Income (first year)",
        "ru-RU": "Опер. прибыль (первый год)",
      },
      tooltip: {
        "en-US": "Operating Income for the first year",
        "ru-RU": "Опер. прибыль за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncome.last": {
      label: {
        "en-US": "Operating Income (current year)",
        "ru-RU": "Опер. прибыль (текущий год)",
      },
      tooltip: {
        "en-US": "Operating Income for the current year",
        "ru-RU": "Опер. прибыль за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncome.sum": {
      label: {
        "en-US": "Operating Income (sum)",
        "ru-RU": "Опер. прибыль (сумма)",
      },
      tooltip: {
        "en-US": "Operating Income (sum for all years)",
        "ru-RU": "Опер. прибыль (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncome.earliest": {
      label: {
        "en-US": "Net Income (first year)",
        "ru-RU": "Чистая прибыль (первый год)",
      },
      tooltip: {
        "en-US": "Net Income for the first year",
        "ru-RU": "Чистая прибыль за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncome.last": {
      label: {
        "en-US": "Net Income (current year)",
        "ru-RU": "Чистая прибыль (текущий год)",
      },
      tooltip: {
        "en-US": "Net Income for the current year",
        "ru-RU": "Чистая прибыль за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncome.sum": {
      label: { "en-US": "Net Income (sum)", "ru-RU": "Чистая прибыль (сумма)" },
      tooltip: {
        "en-US": "Net Income (sum for all years)",
        "ru-RU": "Чистая прибыль (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.weightedAverageShsOutDil.earliest": {
      label: {
        "en-US": "Average Shares (first year)",
        "ru-RU": "Ср. кол-во акций (первый год)",
      },
      tooltip: {
        "en-US": "Average Shares for the first year",
        "ru-RU": "Ср. кол-во акций за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.weightedAverageShsOutDil.last": {
      label: {
        "en-US": "Average Shares (current year)",
        "ru-RU": "Ср. кол-во акций (текущий год)",
      },
      tooltip: {
        "en-US": "Average Shares for the current year",
        "ru-RU": "Ср. кол-во акций за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.weightedAverageShsOutDil.sum": {
      label: {
        "en-US": "Average Shares (sum)",
        "ru-RU": "Ср. кол-во акций (сумма)",
      },
      tooltip: {
        "en-US": "Average Shares (sum for all years)",
        "ru-RU": "Ср. кол-во акций (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.eps.earliest": {
      label: { "en-US": "EPS (first year)", "ru-RU": "EPS (первый год)" },
      tooltip: {
        "en-US": "EPS for the first year",
        "ru-RU": "EPS за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.eps.last": {
      label: { "en-US": "EPS (current year)", "ru-RU": "EPS (текущий год)" },
      tooltip: {
        "en-US": "EPS for the current year",
        "ru-RU": "EPS за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.eps.sum": {
      label: { "en-US": "EPS (sum)", "ru-RU": "EPS (сумма)" },
      tooltip: {
        "en-US": "EPS (sum for all years)",
        "ru-RU": "EPS (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncomePerShare.earliest": {
      label: {
        "en-US": "Operating Income Per Share (first year)",
        "ru-RU": "Опер. прибыль на акцию (первый год)",
      },
      tooltip: {
        "en-US": "Operating Income Per Share for the first year",
        "ru-RU": "Опер. прибыль на акцию за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncomePerShare.last": {
      label: {
        "en-US": "Operating Income Per Share (current year)",
        "ru-RU": "Опер. прибыль на акцию (текущий год)",
      },
      tooltip: {
        "en-US": "Operating Income Per Share for the current year",
        "ru-RU": "Опер. прибыль на акцию за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operatingIncomePerShare.avg": {
      label: {
        "en-US": "Operating Income Per Share (avg)",
        "ru-RU": "Опер. прибыль на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Operating Income Per Share (average for all years)",
        "ru-RU": "Опер. прибыль на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operationalMargin.earliest": {
      label: {
        "en-US": "Operational Margin (first year)",
        "ru-RU": "Опер. маржинальность (первый год)",
      },
      tooltip: {
        "en-US": "Operational Margin for the first year",
        "ru-RU": "Опер. маржинальность за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operationalMargin.last": {
      label: {
        "en-US": "Operational Margin (current year)",
        "ru-RU": "Опер. маржинальность (текущий год)",
      },
      tooltip: {
        "en-US": "Operational Margin for the current year",
        "ru-RU": "Опер. маржинальность за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.operationalMargin.avg": {
      label: {
        "en-US": "Operational Margin (avg)",
        "ru-RU": "Опер. маржинальность (ср.)",
      },
      tooltip: {
        "en-US": "Operational Margin (average for all years)",
        "ru-RU": "Опер. маржинальность (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncomePerShare.earliest": {
      label: {
        "en-US": "Net Income Per Share (first year)",
        "ru-RU": "Чистая прибыль на акцию (первый год)",
      },
      tooltip: {
        "en-US": "Net Income Per Share for the first year",
        "ru-RU": "Чистая прибыль на акцию за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncomePerShare.last": {
      label: {
        "en-US": "Net Income Per Share (current year)",
        "ru-RU": "Чистая прибыль на акцию (текущий год)",
      },
      tooltip: {
        "en-US": "Net Income Per Share for the current year",
        "ru-RU": "Чистая прибыль на акцию за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netIncomePerShare.avg": {
      label: {
        "en-US": "Net Income Per Share (avg)",
        "ru-RU": "Чистая прибыль на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Net Income Per Share (average for all years)",
        "ru-RU": "Чистая прибыль на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netMargin.earliest": {
      label: {
        "en-US": "Net Margin (first year)",
        "ru-RU": "Чистая маржинальность (первый год)",
      },
      tooltip: {
        "en-US": "Net Margin for the first year",
        "ru-RU": "Чистая маржинальность за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netMargin.last": {
      label: {
        "en-US": "Net Margin (current year)",
        "ru-RU": "Чистая маржинальность (текущий год)",
      },
      tooltip: {
        "en-US": "Net Margin for the current year",
        "ru-RU": "Чистая маржинальность за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.netMargin.avg": {
      label: {
        "en-US": "Net Margin (avg)",
        "ru-RU": "Чистая маржинальность (ср.)",
      },
      tooltip: {
        "en-US": "Net Margin (average for all years)",
        "ru-RU": "Чистая маржинальность (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToRevenue.earliest": {
      label: {
        "en-US": "Interest Expense to Revenue (first year)",
        "ru-RU": "% от выручки на платежи по долгам (первый год)",
      },
      tooltip: {
        "en-US": "Interest Expense to Revenue for the first year",
        "ru-RU": "% от выручки на платежи по долгам за первый год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToRevenue.last": {
      label: {
        "en-US": "Interest Expense to Revenue (current year)",
        "ru-RU": "% от выручки на платежи по долгам (текущий год)",
      },
      tooltip: {
        "en-US": "Interest Expense to Revenue for the current year",
        "ru-RU": "% от выручки на платежи по долгам за текущий год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToRevenue.avg": {
      label: {
        "en-US": "Interest Expense to Revenue (avg)",
        "ru-RU": "% от выручки на платежи по долгам (ср.)",
      },
      tooltip: {
        "en-US": "Interest Expense to Revenue (average for all years)",
        "ru-RU": "% от выручки на платежи по долгам (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToOperatingIncome.earliest": {
      label: {
        "en-US": "Interest Expense to Operating Income (first year)",
        "ru-RU": "% от опер. прибыли на платежи по долгам (первый год)",
      },
      tooltip: {
        "en-US": "Interest Expense to Operating Income for the first year",
        "ru-RU": "% от опер. прибыли на платежи по долгам за первый год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToOperatingIncome.last": {
      label: {
        "en-US": "Interest Expense to Operating Income (current year)",
        "ru-RU": "% от опер. прибыли на платежи по долгам (текущий год)",
      },
      tooltip: {
        "en-US": "Interest Expense to Operating Income for the current year",
        "ru-RU": "% от опер. прибыли на платежи по долгам за текущий год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToOperatingIncome.avg": {
      label: {
        "en-US": "Interest Expense to Operating Income (avg)",
        "ru-RU": "% от опер. прибыли на платежи по долгам (ср.)",
      },
      tooltip: {
        "en-US": "Interest Expense to Operating Income (average for all years)",
        "ru-RU":
          "% от опер. прибыли на платежи по долгам (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToFCF.earliest": {
      label: {
        "en-US": "Interest Expense to FCF (first year)",
        "ru-RU": "% от FCF на платежи по долгам (первый год)",
      },
      tooltip: {
        "en-US": "Interest Expense to FCF for the first year",
        "ru-RU": "% от FCF на платежи по долгам за первый год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToFCF.last": {
      label: {
        "en-US": "Interest Expense to FCF (current year)",
        "ru-RU": "% от FCF на платежи по долгам (текущий год)",
      },
      tooltip: {
        "en-US": "Interest Expense to FCF for the current year",
        "ru-RU": "% от FCF на платежи по долгам за текущий год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeStatement.interestExpenseToFCF.avg": {
      label: {
        "en-US": "Interest Expense to FCF (avg)",
        "ru-RU": "% от FCF на платежи по долгам (ср.)",
      },
      tooltip: {
        "en-US": "Interest Expense to FCF (average for all years)",
        "ru-RU": "% от FCF на платежи по долгам (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.netDebt.earliest": {
      label: {
        "en-US": "Net Debt (first year)",
        "ru-RU": "Чистый долг (первый год)",
      },
      tooltip: {
        "en-US": "Net Debt for the first year",
        "ru-RU": "Чистый долг за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.netDebt.last": {
      label: {
        "en-US": "Net Debt (current year)",
        "ru-RU": "Чистый долг (текущий год)",
      },
      tooltip: {
        "en-US": "Net Debt for the current year",
        "ru-RU": "Чистый долг за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.netDebt.sum": {
      label: { "en-US": "Net Debt (sum)", "ru-RU": "Чистый долг (сумма)" },
      tooltip: {
        "en-US": "Net Debt (sum for all years)",
        "ru-RU": "Чистый долг (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.totalEquity.earliest": {
      label: { "en-US": "Equity (first year)", "ru-RU": "Equity (первый год)" },
      tooltip: {
        "en-US": "Equity for the first year",
        "ru-RU": "Equity за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.totalEquity.last": {
      label: {
        "en-US": "Equity (current year)",
        "ru-RU": "Equity (текущий год)",
      },
      tooltip: {
        "en-US": "Equity for the current year",
        "ru-RU": "Equity за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.totalEquity.sum": {
      label: { "en-US": "Equity (sum)", "ru-RU": "Equity (сумма)" },
      tooltip: {
        "en-US": "Equity (sum for all years)",
        "ru-RU": "Equity (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToEquity.earliest": {
      label: {
        "en-US": "Debt/Equity (first year)",
        "ru-RU": "Долг к капиталу (первый год)",
      },
      tooltip: {
        "en-US": "Debt/Equity Ratio for the first year",
        "ru-RU": "Коэффициент долга к капиталу за первый год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToEquity.last": {
      label: {
        "en-US": "Debt/Equity (current year)",
        "ru-RU": "Долг к капиталу (текущий год)",
      },
      tooltip: {
        "en-US": "Debt/Equity Ratio for the current year",
        "ru-RU": "Коэффициент долга к капиталу за текущий год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToEquity.avg": {
      label: { "en-US": "Debt/Equity (avg)", "ru-RU": "Долг к капиталу (ср.)" },
      tooltip: {
        "en-US": "Debt/Equity Ratio (average for all years)",
        "ru-RU": "Коэффициент долга к капиталу (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToShares.earliest": {
      label: {
        "en-US": "Debt to Shares (first year)",
        "ru-RU": "Долг на акцию (первый год)",
      },
      tooltip: {
        "en-US": "Debt to Shares Ratio for the first year",
        "ru-RU": "Коэффициент долга на акцию за первый год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToShares.last": {
      label: {
        "en-US": "Debt to Shares (current year)",
        "ru-RU": "Долг на акцию (текущий год)",
      },
      tooltip: {
        "en-US": "Debt to Shares Ratio for the current year",
        "ru-RU": "Коэффициент долга на акцию за текущий год",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.debtToShares.avg": {
      label: {
        "en-US": "Debt to Shares (avg)",
        "ru-RU": "Долг на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Debt to Shares Ratio (average for all years)",
        "ru-RU": "Коэффициент долга на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.equityToSharesRatio.earliest": {
      label: {
        "en-US": "Equity to Shares Ratio (first year)",
        "ru-RU": "Equity на акцию (первый год)",
      },
      tooltip: {
        "en-US": "Equity to Shares Ratio for the first year",
        "ru-RU": "Equity на акцию за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.equityToSharesRatio.last": {
      label: {
        "en-US": "Equity to Shares Ratio (current year)",
        "ru-RU": "Equity на акцию (текущий год)",
      },
      tooltip: {
        "en-US": "Equity to Shares Ratio for the current year",
        "ru-RU": "Equity на акцию за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.balance.equityToSharesRatio.avg": {
      label: {
        "en-US": "Equity to Shares Ratio (avg)",
        "ru-RU": "Equity на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Equity to Shares Ratio (avg for all years)",
        "ru-RU": "Equity на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.freeCashFlow.earliest": {
      label: {
        "en-US": "Free Cash Flow (first year)",
        "ru-RU": "Денежный поток (первый год)",
      },
      tooltip: {
        "en-US": "Free Cash Flow for the first year",
        "ru-RU": "Денежный поток за первый год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.freeCashFlow.last": {
      label: {
        "en-US": "Free Cash Flow (current year)",
        "ru-RU": "Денежный поток (текущий год)",
      },
      tooltip: {
        "en-US": "Free Cash Flow for the current year",
        "ru-RU": "Денежный поток за текущий год",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.freeCashFlow.sum": {
      label: {
        "en-US": "Free Cash Flow (sum)",
        "ru-RU": "Денежный поток (сумма)",
      },
      tooltip: {
        "en-US": "Free Cash Flow (sum for all years)",
        "ru-RU": "Денежный поток (сумма за все годы)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.FCFPerShare.earliest": {
      label: {
        "en-US": "Free Cash Flow per Share (first year)",
        "ru-RU": "FCF на акцию (первый год)",
      },
      tooltip: {
        "en-US": "Free Cash Flow per Share for the first year",
        "ru-RU": "FCF на акцию за первый год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.FCFPerShare.last": {
      label: {
        "en-US": "Free Cash Flow per Share (current year)",
        "ru-RU": "FCF на акцию (текущий год)",
      },
      tooltip: {
        "en-US": "Free Cash Flow per Share for the current year",
        "ru-RU": "FCF на акцию за текущий год",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.cashFlow.FCFPerShare.avg": {
      label: {
        "en-US": "Free Cash Flow per Share (avg)",
        "ru-RU": "FCF на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Free Cash Flow per Share (average for all years)",
        "ru-RU": "FCF на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.ratios.priceToEarningsRatioTTM": {
      label: {
        "en-US": "Price to Earnings Ratio (TTM)",
        "ru-RU": "P/E (тек.)",
      },
      tooltip: {
        "en-US": "Price to Earnings Ratio (TTM)",
        "ru-RU": "P/E (текущий)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.ratios.priceToBookRatioTTM": {
      label: { "en-US": "Price to Book Ratio (TTM)", "ru-RU": "P/B (тек.)" },
      tooltip: {
        "en-US": "Price to Book Ratio (TTM)",
        "ru-RU": "P/B (текущий)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROE.earliest": {
      label: { "en-US": "ROE (first year)", "ru-RU": "ROE (первый год)" },
      tooltip: {
        "en-US": "ROE for the first year",
        "ru-RU": "ROE за первый год",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROE.last": {
      label: { "en-US": "ROE (current year)", "ru-RU": "ROE (текущий год)" },
      tooltip: {
        "en-US": "ROE for the current year",
        "ru-RU": "ROE за текущий год",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROE.avg": {
      label: { "en-US": "ROE (avg)", "ru-RU": "ROE (ср.)" },
      tooltip: {
        "en-US": "ROE (average for all years)",
        "ru-RU": "ROE (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.netROE.avg": {
      label: { "en-US": "Net ROE (avg)", "ru-RU": "Чистый ROE (ср.)" },
      tooltip: {
        "en-US": "Net ROE (average for all years)",
        "ru-RU": "Чистый ROE (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROEPriceAdapted.avg": {
      label: {
        "en-US": "ROE price adapted (avg)",
        "ru-RU": "ROE с поправкой на цену акции (ср.)",
      },
      tooltip: {
        "en-US": "ROE price adapted (average for all years)",
        "ru-RU": "ROE с поправкой на цену акции (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.netROEPriceAdapted.avg": {
      label: {
        "en-US": "Net ROE price adapted (avg)",
        "ru-RU": "Чистый ROE с поправкой на цену акции (ср.)",
      },
      tooltip: {
        "en-US": "Net ROE price adapted (average for all years)",
        "ru-RU": "Чистый ROE с поправкой на цену акции (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROFCFE.earliest": {
      label: { "en-US": "ROFCFE (first year)", "ru-RU": "ROFCFE (первый год)" },
      tooltip: {
        "en-US": "ROFCFE for the first year",
        "ru-RU": "ROFCFE за первый год",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROFCFE.last": {
      label: {
        "en-US": "ROFCFE (current year)",
        "ru-RU": "ROFCFE (текущий год)",
      },
      tooltip: {
        "en-US": "ROFCFE for the current year",
        "ru-RU": "ROFCFE за текущий год",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROFCFE.avg": {
      label: { "en-US": "ROFCFE (avg)", "ru-RU": "ROFCFE (ср.)" },
      tooltip: {
        "en-US": "ROFCFE (average for all years)",
        "ru-RU": "ROFCFE (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.netROFCFE.avg": {
      label: { "en-US": "Net ROFCFE (avg)", "ru-RU": "Чистый ROFCFE (ср.)" },
      tooltip: {
        "en-US": "Net ROFCFE (average for all years)",
        "ru-RU": "Чистый ROFCFE (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.ROFCFEPriceAdapted.avg": {
      label: {
        "en-US": "ROFCFE price adapted (avg)",
        "ru-RU": "ROFCFE с поправкой на цену акции (ср.)",
      },
      tooltip: {
        "en-US": "ROFCFE price adapted (average for all years)",
        "ru-RU": "ROFCFE с поправкой на цену акции (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.netROFCFEPriceAdapted.avg": {
      label: {
        "en-US": "Net ROFCFE price adapted (avg)",
        "ru-RU": "Чистый ROFCFE с поправкой на цену акции (ср.)",
      },
      tooltip: {
        "en-US": "Net ROFCFE price adapted (average for all years)",
        "ru-RU":
          "Чистый ROFCFE с поправкой на цену акции (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.growthRates.netIncomePerShare.avg": {
      label: {
        "en-US": "Net Income per Share Growth Rate (avg)",
        "ru-RU": "Темп роста чистой прибыли на акцию (ср.)",
      },
      tooltip: {
        "en-US": "Net Income per Share Growth Rate (average for all years)",
        "ru-RU": "Темп роста чистой прибыли на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.growthRates.netIncome.avg": {
      label: {
        "en-US": "Net Income Growth Rate (avg)",
        "ru-RU": "Темп роста чистой прибыли (ср.)",
      },
      tooltip: {
        "en-US": "Net Income Growth Rate (average for all years)",
        "ru-RU": "Темп роста чистой прибыли (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.growthRates.operatingIncome.avg": {
      label: {
        "en-US": "Operating Income Growth Rate (avg)",
        "ru-RU": "Темп роста опер. прибыли (ср.)",
      },
      tooltip: {
        "en-US": "Operating Income Growth Rate (average for all years)",
        "ru-RU": "Темп роста опер. прибыли (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.coefs.growthRates.FCFPerShare.avg": {
      label: {
        "en-US": "FCF per Share Growth Rate (avg)",
        "ru-RU": "Темп роста FCF на акцию (ср.)",
      },
      tooltip: {
        "en-US": "FCF per Share Growth Rate (average for all years)",
        "ru-RU": "Темп роста FCF на акцию (средняя за все годы)",
      },
      headerColor: COLOR_HEADER_TURQUOISE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_TURQUOISE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.dealIncome.perNetIncome": {
      label: { "en-US": "Deal Income (NI)", "ru-RU": "Доходность сделки (ЧП)" },
      tooltip: {
        "en-US": "Deal Income per Net Income",
        "ru-RU": "Доходность сделки по чистой прибыли",
      },
      headerColor: COLOR_HEADER_DARK_BLUE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_BLUE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.dealIncome.perFcf": {
      label: {
        "en-US": "Deal income (FCF)",
        "ru-RU": "Доходность сделки (FCF)",
      },
      tooltip: {
        "en-US": "Deal income per FCF",
        "ru-RU": "Доходность сделки (по FCF)",
      },
      headerColor: COLOR_HEADER_DARK_BLUE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_BLUE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.dealIncome.perFcfAndNetIncome": {
      label: {
        "en-US": "Deal income (FCF*NI)",
        "ru-RU": "Доходность сделки (FCF*ЧП)",
      },
      tooltip: {
        "en-US": "Deal income per FCF*NI",
        "ru-RU": "Доходность сделки (по FCF*ЧП)",
      },
      headerColor: COLOR_HEADER_DARK_BLUE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_BLUE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.values.expectedIncomeGrowthRate": {
      label: {
        "en-US": "Expected Income Growth Rate",
        "ru-RU": "Ожидаемый темп роста прибыли",
      },
      tooltip: {
        "en-US": "Expected Income Growth Rate (based on country)",
        "ru-RU": "Ожидаемый темп роста прибыл (зависит от страны)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.values.discountRate": {
      label: { "en-US": "Buy Rate", "ru-RU": "Коэффициент покупки" },
      tooltip: {
        "en-US": "Necessary discount for buy (%)",
        "ru-RU": "Требуемая скидка при покупке (%)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.values.marginOfSafety": {
      label: { "en-US": "Margin of Safety", "ru-RU": "Маржа безопасности" },
      tooltip: {
        "en-US": "Margin of Safety (%)",
        "ru-RU": "Маржа безопасности (%)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.values.saleCoefficient": {
      label: { "en-US": "Sale Coefficient", "ru-RU": "Коэффициент продажи" },
      tooltip: {
        "en-US": "Necessary surplus for sell (%)",
        "ru-RU": "Требуемая надбавка для продажи (%)",
      },
      headerColor: COLOR_HEADER_WHITE,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_WHITE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.incomeType": {
      label: { "en-US": "Income Type", "ru-RU": "Тип прибыли" },
      tooltip: { "en-US": "Income Type", "ru-RU": "Тип прибыли" },
      headerColor: COLOR_HEADER_DARK_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.expectedIncome": {
      label: { "en-US": "Expected Income", "ru-RU": "Ожид. доход" },
      tooltip: { "en-US": "Expected Income", "ru-RU": "Ожидаемый доход" },
      headerColor: COLOR_HEADER_DARK_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.expectedIncomeGrowthRate": {
      label: {
        "en-US": "Expected Income Growth Rate",
        "ru-RU": "Ожид. темп роста дохода",
      },
      tooltip: {
        "en-US": "Expected Income Growth Rate",
        "ru-RU": "Ожид. темп роста дохода",
      },
      headerColor: COLOR_HEADER_DARK_VIOLET,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_VIOLET,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price1": {
      label: { "en-US": "Fair Price №1", "ru-RU": "Справ. стоимость №1" },
      tooltip: {
        "en-US": "Fair Price №1",
        "ru-RU": "Справедливая стоимость №1",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price2": {
      label: { "en-US": "Fair Price №2", "ru-RU": "Справ. стоимость №2" },
      tooltip: {
        "en-US": "Fair Price №2",
        "ru-RU": "Справедливая стоимость №2",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price3": {
      label: { "en-US": "Fair Price №3", "ru-RU": "Справ. стоимость №3" },
      tooltip: {
        "en-US": "Fair Price №3",
        "ru-RU": "Справедливая стоимость №3",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price4": {
      label: { "en-US": "Fair Price №4", "ru-RU": "Справ. стоимость №4" },
      tooltip: {
        "en-US": "Fair Price №4",
        "ru-RU": "Справедливая стоимость №4",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price5": {
      label: { "en-US": "Fair Price №5", "ru-RU": "Справ. стоимость №5" },
      tooltip: {
        "en-US": "Fair Price №5",
        "ru-RU": "Справедливая стоимость №5",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price6": {
      label: { "en-US": "Fair Price №6", "ru-RU": "Справ. стоимость №6" },
      tooltip: {
        "en-US": "Fair Price №6",
        "ru-RU": "Справедливая стоимость №6",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price7": {
      label: { "en-US": "Fair Price №7", "ru-RU": "Справ. стоимость №7" },
      tooltip: {
        "en-US": "Fair Price №7",
        "ru-RU": "Справедливая стоимость №7",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.price8": {
      label: { "en-US": "Fair Price №8", "ru-RU": "Справ. стоимость №8" },
      tooltip: {
        "en-US": "Fair Price №8",
        "ru-RU": "Справедливая стоимость №8",
      },
      headerColor: COLOR_HEADER_YELLOW,
      headerTextColor: COLOR_TEXT_DARK_GRAY,
      cellColor: COLOR_CELL_YELLOW,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price1": {
      label: { "en-US": "Buy Price №1", "ru-RU": "Стоимость покупки №1" },
      tooltip: { "en-US": "Buy Price №1", "ru-RU": "Стоимость покупки №1" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price2": {
      label: { "en-US": "Buy Price №2", "ru-RU": "Стоимость покупки №2" },
      tooltip: { "en-US": "Buy Price №2", "ru-RU": "Стоимость покупки №2" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price3": {
      label: { "en-US": "Buy Price №3", "ru-RU": "Стоимость покупки №3" },
      tooltip: { "en-US": "Buy Price №3", "ru-RU": "Стоимость покупки №3" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price4": {
      label: { "en-US": "Buy Price №4", "ru-RU": "Стоимость покупки №4" },
      tooltip: { "en-US": "Buy Price №4", "ru-RU": "Стоимость покупки №4" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price5": {
      label: { "en-US": "Buy Price №5", "ru-RU": "Стоимость покупки №5" },
      tooltip: { "en-US": "Buy Price №5", "ru-RU": "Стоимость покупки №5" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price6": {
      label: { "en-US": "Buy Price №6", "ru-RU": "Стоимость покупки №6" },
      tooltip: { "en-US": "Buy Price №6", "ru-RU": "Стоимость покупки №6" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price7": {
      label: { "en-US": "Buy Price №7", "ru-RU": "Стоимость покупки №7" },
      tooltip: { "en-US": "Buy Price №7", "ru-RU": "Стоимость покупки №7" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.price8": {
      label: { "en-US": "Buy Price №8", "ru-RU": "Стоимость покупки №8" },
      tooltip: { "en-US": "Buy Price №8", "ru-RU": "Стоимость покупки №8" },
      headerColor: COLOR_HEADER_DARK_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_DARK_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price1": {
      label: { "en-US": "Sell Price №1", "ru-RU": "Стоимость продажи №1" },
      tooltip: { "en-US": "Sell Price №1", "ru-RU": "Стоимость продажи №1" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price2": {
      label: { "en-US": "Sell Price №2", "ru-RU": "Стоимость продажи №2" },
      tooltip: { "en-US": "Sell Price №2", "ru-RU": "Стоимость продажи №2" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price3": {
      label: { "en-US": "Sell Price №3", "ru-RU": "Стоимость продажи №3" },
      tooltip: { "en-US": "Sell Price №3", "ru-RU": "Стоимость продажи №3" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price4": {
      label: { "en-US": "Sell Price №4", "ru-RU": "Стоимость продажи №4" },
      tooltip: { "en-US": "Sell Price №4", "ru-RU": "Стоимость продажи №4" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price5": {
      label: { "en-US": "Sell Price №5", "ru-RU": "Стоимость продажи №5" },
      tooltip: { "en-US": "Sell Price №5", "ru-RU": "Стоимость продажи №5" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price6": {
      label: { "en-US": "Sell Price №6", "ru-RU": "Стоимость продажи №6" },
      tooltip: { "en-US": "Sell Price №6", "ru-RU": "Стоимость продажи №6" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price7": {
      label: { "en-US": "Sell Price №7", "ru-RU": "Стоимость продажи №7" },
      tooltip: { "en-US": "Sell Price №7", "ru-RU": "Стоимость продажи №7" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.price8": {
      label: { "en-US": "Sell Price №8", "ru-RU": "Стоимость продажи №8" },
      tooltip: { "en-US": "Sell Price №8", "ru-RU": "Стоимость продажи №8" },
      headerColor: COLOR_HEADER_GREEN,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GREEN,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.broad.min": {
      label: {
        "en-US": "[B] Min. fair price",
        "ru-RU": "[Ш] Мин. справ. стоимость",
      },
      tooltip: {
        "en-US": "[B] Min. fair price",
        "ru-RU": "[Ш] Мин. справ. стоимость",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.broad.max": {
      label: {
        "en-US": "[B] Max. fair price",
        "ru-RU": "[Ш] Макс. справ. стоимость",
      },
      tooltip: {
        "en-US": "[B] Max. fair price",
        "ru-RU": "[Ш] Макс. справ. стоимость",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.broad.min": {
      label: {
        "en-US": "[B] Min. buy price",
        "ru-RU": "[Ш] Мин. стоимость покупки",
      },
      tooltip: {
        "en-US": "[B] Min. buy price",
        "ru-RU": "[Ш] Мин. стоимость покупки",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.broad.max": {
      label: {
        "en-US": "[B] Max. buy price",
        "ru-RU": "[Ш] Макс. стоимость покупки",
      },
      tooltip: {
        "en-US": "[B] Max. buy price",
        "ru-RU": "[Ш] Макс. стоимость покупки",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.broad.min": {
      label: {
        "en-US": "[B] Min. sell price",
        "ru-RU": "[Ш] Мин. стоимость продажи",
      },
      tooltip: {
        "en-US": "[B] Min. sell price",
        "ru-RU": "[Ш] Мин. стоимость продажи",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.broad.max": {
      label: {
        "en-US": "[B] Max. sell price",
        "ru-RU": "[Ш] Макс. стоимость продажи",
      },
      tooltip: {
        "en-US": "[B] Max. sell price",
        "ru-RU": "[Ш] Макс. стоимость продажи",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.narrow.min": {
      label: {
        "en-US": "[N] Min. fair price",
        "ru-RU": "[У] Мин. справ. стоимость",
      },
      tooltip: {
        "en-US": "[N] Min. fair price",
        "ru-RU": "[У] Мин. справ. стоимость",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.fair.narrow.max": {
      label: {
        "en-US": "[N] Max. fair price",
        "ru-RU": "[У] Макс. справ. стоимость",
      },
      tooltip: {
        "en-US": "[N] Max. fair price",
        "ru-RU": "[У] Макс. справ. стоимость",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.narrow.min": {
      label: {
        "en-US": "[N] Min. buy price",
        "ru-RU": "[У] Мин. стоимость покупки",
      },
      tooltip: {
        "en-US": "[N] Min. buy price",
        "ru-RU": "[У] Мин. стоимость покупки",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.buy.narrow.max": {
      label: {
        "en-US": "[N] Max. buy price",
        "ru-RU": "[У] Макс. стоимость покупки",
      },
      tooltip: {
        "en-US": "[N] Max. buy price",
        "ru-RU": "[У] Макс. стоимость покупки",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.narrow.min": {
      label: {
        "en-US": "[N] Min. sell price",
        "ru-RU": "[У] Мин. стоимость продажи",
      },
      tooltip: {
        "en-US": "[N] Min. sell price",
        "ru-RU": "[У] Мин. стоимость продажи",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.prices.sell.narrow.max": {
      label: {
        "en-US": "[N] Max. sell price",
        "ru-RU": "[У] Макс. стоимость продажи",
      },
      tooltip: {
        "en-US": "[N] Max. sell price",
        "ru-RU": "[У] Макс. стоимость продажи",
      },
      headerColor: COLOR_HEADER_ORANGE,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_ORANGE,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.flags.isInterestingPrice": {
      label: { "en-US": "Is Interesting Price?", "ru-RU": "Интересная цена?" },
      tooltip: {
        "en-US": "Is Interesting Price?",
        "ru-RU": "Интересная цена?",
      },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.flags.isNoInterestStatus": {
      label: { "en-US": "No Interest?", "ru-RU": "Не интересно?" },
      tooltip: { "en-US": "No Interest?", "ru-RU": "Не интересно?" },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.flags.isInterestingPriceBroadStatus": {
      label: {
        "en-US": "[B] Is Interesting Price?",
        "ru-RU": "[Ш] Интересная цена (широкий разброс)?",
      },
      tooltip: {
        "en-US": "Is Interesting Price (broad)?",
        "ru-RU": "Интересная цена (широкий разброс)?",
      },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.flags.isExpensiveStatus": {
      label: { "en-US": "Is Expensive Price?", "ru-RU": "Дорого?" },
      tooltip: { "en-US": "Is Expensive Price?", "ru-RU": "Дорого?" },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    "financials.flags.isVeryExpensiveStatus": {
      label: { "en-US": "Is Very Expensive Price?", "ru-RU": "Очень дорого?" },
      tooltip: {
        "en-US": "Is Very Expensive Price?",
        "ru-RU": "Очень дорого?",
      },
      headerColor: COLOR_HEADER_RED,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_RED,
      cellTextColor: COLOR_TEXT_BLACK,
    },
  },
  availableFilters: [
    {
      propertyKey: "apiData.fmp.updatedAt",
      order: 1,
      uiType: "dateRange",
      format: {
        label: { "en-US": "IUpdatedAt", "ru-RU": "Дата обновления" },
        tooltip: {
          "en-US": "When last time updated",
          "ru-RU": "Когда в последний раз обновлялось",
        },
      },
      conditions: [],
    },
    {
      propertyKey: "symbol",
      order: 2,
      uiType: "like",
      format: {
        label: { "en-US": "Symbol", "ru-RU": "Тикер" },
        tooltip: { "en-US": "Symbol", "ru-RU": "Символ" },
      },
      conditions: [],
    },
    {
      propertyKey: "relations.company.name",
      order: 3,
      uiType: "like",
      format: {
        label: { "en-US": "Name", "ru-RU": "Название" },
        tooltip: { "en-US": "Company Name", "ru-RU": "Название компании" },
      },
      conditions: [],
    },
    {
      propertyKey: "autoStatus",
      order: 3,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Status: System", "ru-RU": "Статус: Система" },
        tooltip: { "en-US": "System status", "ru-RU": "Системный статус" },
      },
      conditions: [],
    },
    {
      propertyKey: "manualStatus",
      order: 3,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Status: My", "ru-RU": "Статус: Мой" },
        tooltip: { "en-US": "Manual status", "ru-RU": "Ручной статус" },
      },
      conditions: [],
    },
    {
      propertyKey: "relations.tradeCurrency.name",
      order: 3,
      uiType: "select",
      format: {
        label: { "en-US": "Currency", "ru-RU": "Валюта" },
        tooltip: { "en-US": "Trade Currency", "ru-RU": "Валюта торговли" },
      },
      conditions: [],
    },
    {
      propertyKey: "relations.exchange.key",
      order: 3,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Exchange", "ru-RU": "Биржа" },
        tooltip: { "en-US": "Exchange", "ru-RU": "Биржа" },
      },
      conditions: [],
    },
    {
      propertyKey: "financials.flags.isInterestingPrice",
      order: 3,
      uiType: "select",
      format: {
        label: {
          "en-US": "Is Interesting Price?",
          "ru-RU": "Интересная цена?",
        },
        tooltip: {
          "en-US": "Is Interesting Price?",
          "ru-RU": "Интересная цена?",
        },
      },
      conditions: [],
    },
  ],
  lists: [
    {
      propertyKey: "autoStatus",
      items: AUTO_STATUS.map((value) => ({
        value,
        label: AUTO_STATUS_LABELS[value],
      })),
    },
    {
      propertyKey: "manualStatus",
      items: MANUAL_STATUS.map((value) => ({
        value,
        label: MANUAL_STATUS_LABELS[value],
      })),
    },
    {
      propertyKey: "relations.tradeCurrency.name",
      items: [
        { value: "USD", label: { "en-US": "USD", "ru-RU": "USD" } },
        { value: "HKD", label: { "en-US": "HKD", "ru-RU": "HKD" } },
        { value: "RUB", label: { "en-US": "RUB", "ru-RU": "RUB" } },
        { value: "DEFAULT", label: { "en-US": "DEFAULT", "ru-RU": "DEFAULT" } },
      ],
    },
    {
      propertyKey: "relations.exchange.key",
      items: [
        { value: "NASDAQ", label: { "en-US": "NASDAQ", "ru-RU": "NASDAQ" } },
        { value: "NYSE", label: { "en-US": "NYSE", "ru-RU": "NYSE" } },
        { value: "AMEX", label: { "en-US": "AMEX", "ru-RU": "AMEX" } },
        { value: "PNK", label: { "en-US": "PNK", "ru-RU": "PNK" } },
        { value: "OTC", label: { "en-US": "OTC", "ru-RU": "OTC" } },
        { value: "HKSE", label: { "en-US": "HKSE", "ru-RU": "HKSE" } },
      ],
    },
    {
      propertyKey: "financials.flags.isInterestingPrice",
      items: [
        { value: "true", label: { "en-US": "Yes", "ru-RU": "Да" } },
        { value: "false", label: { "en-US": "No", "ru-RU": "Нет" } },
      ],
    },
    {
      propertyKey: "financials.incomeType",
      items: [
        { value: "netIncome", label: { "en-US": "Net", "ru-RU": "ЧП" } },
        { value: "cfIncome", label: { "en-US": "CF", "ru-RU": "ПС" } },
      ],
    },
  ],
  columnGroups: [
    {
      id: "data-received",
      label: "Дата обновления",
      columnIds: ["apiData.fmp.updatedAt", "updatedAt"],
    },
    {
      id: "basic",
      label: "Базовая информация",
      columnIds: [
        "symbol",
        "autoStatus",
        "manualStatus",
        "relations.company.name",
        "relations.exchange.key",
        "relations.tradeCurrency.name",
        "relations.reportedCurrency.name",
      ],
    },
    {
      id: "price",
      label: "Цена",
      columnIds: ["financials.price"],
    },
    {
      id: "years",
      label: "Годы",
      columnIds: [
        "financials.years.earliest",
        "financials.years.last",
        "financials.years.sum",
      ],
      collapsedByDefault: true,
    },
    {
      id: "income",
      label: "Доходы",
      columnIds: [
        "financials.incomeStatement.revenue.earliest",
        "financials.incomeStatement.revenue.last",
        "financials.incomeStatement.revenue.sum",
        "financials.incomeStatement.interestExpense.earliest",
        "financials.incomeStatement.interestExpense.last",
        "financials.incomeStatement.interestExpense.sum",
        "financials.incomeStatement.operatingIncome.earliest",
        "financials.incomeStatement.operatingIncome.last",
        "financials.incomeStatement.operatingIncome.sum",
        "financials.incomeStatement.netIncome.earliest",
        "financials.incomeStatement.netIncome.last",
        "financials.incomeStatement.netIncome.sum",
        "financials.incomeStatement.weightedAverageShsOutDil.earliest",
        "financials.incomeStatement.weightedAverageShsOutDil.last",
        "financials.incomeStatement.weightedAverageShsOutDil.sum",
        "financials.incomeStatement.eps.earliest",
        "financials.incomeStatement.eps.last",
        "financials.incomeStatement.eps.sum",
      ],
      collapsedByDefault: true,
    },
    {
      id: "per-share",
      label: "На акцию",
      columnIds: [
        "financials.incomeStatement.operatingIncomePerShare.earliest",
        "financials.incomeStatement.operatingIncomePerShare.last",
        "financials.incomeStatement.operatingIncomePerShare.avg",
        "financials.incomeStatement.operationalMargin.earliest",
        "financials.incomeStatement.operationalMargin.last",
        "financials.incomeStatement.operationalMargin.avg",
        "financials.incomeStatement.netIncomePerShare.earliest",
        "financials.incomeStatement.netIncomePerShare.last",
        "financials.incomeStatement.netIncomePerShare.avg",
        "financials.incomeStatement.netMargin.earliest",
        "financials.incomeStatement.netMargin.last",
        "financials.incomeStatement.netMargin.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "interest-ratios",
      label: "Платежи по долгам (%)",
      columnIds: [
        "financials.incomeStatement.interestExpenseToRevenue.earliest",
        "financials.incomeStatement.interestExpenseToRevenue.last",
        "financials.incomeStatement.interestExpenseToRevenue.avg",
        "financials.incomeStatement.interestExpenseToOperatingIncome.earliest",
        "financials.incomeStatement.interestExpenseToOperatingIncome.last",
        "financials.incomeStatement.interestExpenseToOperatingIncome.avg",
        "financials.incomeStatement.interestExpenseToFCF.earliest",
        "financials.incomeStatement.interestExpenseToFCF.last",
        "financials.incomeStatement.interestExpenseToFCF.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "balance",
      label: "Баланс",
      columnIds: [
        "financials.balance.netDebt.earliest",
        "financials.balance.netDebt.last",
        "financials.balance.netDebt.sum",
        "financials.balance.totalEquity.earliest",
        "financials.balance.totalEquity.last",
        "financials.balance.totalEquity.sum",
        "financials.balance.debtToEquity.earliest",
        "financials.balance.debtToEquity.last",
        "financials.balance.debtToEquity.avg",
        "financials.balance.debtToShares.earliest",
        "financials.balance.debtToShares.last",
        "financials.balance.debtToShares.avg",
        "financials.balance.equityToSharesRatio.earliest",
        "financials.balance.equityToSharesRatio.last",
        "financials.balance.equityToSharesRatio.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "cash-flow",
      label: "Денежный поток",
      columnIds: [
        "financials.cashFlow.freeCashFlow.earliest",
        "financials.cashFlow.freeCashFlow.last",
        "financials.cashFlow.freeCashFlow.sum",
        "financials.cashFlow.FCFPerShare.earliest",
        "financials.cashFlow.FCFPerShare.last",
        "financials.cashFlow.FCFPerShare.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "ratios",
      label: "Оценка",
      columnIds: [
        "financials.ratios.priceToEarningsRatioTTM",
        "financials.ratios.priceToBookRatioTTM",
      ],
      collapsedByDefault: true,
    },
    {
      id: "roe",
      label: "ROE",
      columnIds: [
        "financials.coefs.ROE.earliest",
        "financials.coefs.ROE.last",
        "financials.coefs.ROE.avg",
        "financials.coefs.netROE.avg",
        "financials.coefs.ROEPriceAdapted.avg",
        "financials.coefs.netROEPriceAdapted.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "rofcfe",
      label: "ROFCFE",
      columnIds: [
        "financials.coefs.ROFCFE.earliest",
        "financials.coefs.ROFCFE.last",
        "financials.coefs.ROFCFE.avg",
        "financials.coefs.netROFCFE.avg",
        "financials.coefs.ROFCFEPriceAdapted.avg",
        "financials.coefs.netROFCFEPriceAdapted.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "growth-rates",
      label: "Темп роста",
      columnIds: [
        "financials.coefs.growthRates.netIncomePerShare.avg",
        "financials.coefs.growthRates.netIncome.avg",
        "financials.coefs.growthRates.operatingIncome.avg",
        "financials.coefs.growthRates.FCFPerShare.avg",
      ],
      collapsedByDefault: true,
    },
    {
      id: "deal-income",
      label: "Сделка",
      columnIds: [
        "financials.dealIncome.perNetIncome",
        "financials.dealIncome.perFcf",
        "financials.dealIncome.perFcfAndNetIncome",
      ],
      collapsedByDefault: true,
    },
    {
      id: "expectations",
      label: "Ожидания",
      columnIds: [
        "financials.expectedIncome",
        "financials.expectedIncomeGrowthRate",
        "financials.incomeType",
      ],
      collapsedByDefault: true,
    },
    {
      id: "values",
      label: "Экономические значения",
      columnIds: [
        "financials.values.expectedIncomeGrowthRate",
        "financials.values.discountRate",
        "financials.values.marginOfSafety",
        "financials.values.saleCoefficient",
      ],
      collapsedByDefault: true,
    },
    {
      id: "prices-fair",
      label: "Справедливая стоимость",
      columnIds: [
        "financials.prices.fair.price1",
        "financials.prices.fair.price2",
        "financials.prices.fair.price3",
        "financials.prices.fair.price4",
        "financials.prices.fair.price5",
        "financials.prices.fair.price6",
        "financials.prices.fair.price7",
        "financials.prices.fair.price8",
        "financials.prices.fair.broad.min",
        "financials.prices.fair.broad.max",
        "financials.prices.fair.narrow.min",
        "financials.prices.fair.narrow.max",
      ],
      collapsedByDefault: true,
    },
    {
      id: "prices-buy",
      label: "Стоимость покупки",
      columnIds: [
        "financials.prices.buy.price1",
        "financials.prices.buy.price2",
        "financials.prices.buy.price3",
        "financials.prices.buy.price4",
        "financials.prices.buy.price5",
        "financials.prices.buy.price6",
        "financials.prices.buy.price7",
        "financials.prices.buy.price8",
        "financials.prices.buy.broad.min",
        "financials.prices.buy.broad.max",
        "financials.prices.buy.narrow.min",
        "financials.prices.buy.narrow.max",
      ],
      collapsedByDefault: true,
    },
    {
      id: "prices-sell",
      label: "Стоимость продажи",
      columnIds: [
        "financials.prices.sell.price1",
        "financials.prices.sell.price2",
        "financials.prices.sell.price3",
        "financials.prices.sell.price4",
        "financials.prices.sell.price5",
        "financials.prices.sell.price6",
        "financials.prices.sell.price7",
        "financials.prices.sell.price8",
        "financials.prices.sell.broad.min",
        "financials.prices.sell.broad.max",
        "financials.prices.sell.narrow.min",
        "financials.prices.sell.narrow.max",
      ],
      collapsedByDefault: true,
    },
    {
      id: "flags",
      label: "Вердикты",
      columnIds: [
        "financials.flags.isInterestingPrice",
        "financials.flags.isNoInterestStatus",
        "financials.flags.isInterestingPriceBroadStatus",
        "financials.flags.isExpensiveStatus",
        "financials.flags.isVeryExpensiveStatus",
      ],
      collapsedByDefault: true,
    },
    {
      id: "api-profile",
      label: "API Profile",
      columnIds: [
        "apiData.fmp.companyOutlook.profile.companyName",
        "apiData.fmp.companyOutlook.profile.description",
        "apiData.fmp.companyOutlook.profile.industry",
        "apiData.fmp.companyOutlook.profile.country",
        "apiData.fmp.companyOutlook.profile.currency",
      ],
      collapsedByDefault: true,
    },
    {
      id: "existing-extra",
      label: "Доп. колонки",
      columnIds: [
        "exchange",
        "price",
        "marketCap",
        "volume",
        "avgVolume",
        "sharesOutstanding",
        "relations.industry.name",
        "relations.country.name",
      ],
      collapsedByDefault: true,
    },
  ],
  defaultFilter: {
    column: "symbol",
    operator: "contains",
  },
  version: 4,
  isSeed: true,
};
